import { FUNCTIONS, type Node, parseExpression } from './parse';

/**
 * A formula, written out for the label beside its curve.
 *
 * ## Why this exists rather than drawing the LaTeX
 *
 * MathLive and KaTeX render to **HTML** — nested spans, positioned by CSS, using the browser's
 * font engine. The canvas has none of that, and neither do the SVG and PDF exporters, which
 * are hand-written and single-line. Getting true stacked fractions onto all three would mean
 * building a maths typesetter, which is a project rather than a feature.
 *
 * So the panel shows the real thing — the formula editor renders it properly, which is where a
 * user is actually reading it while they work — and the canvas gets a faithful single-line
 * form in the notation the label engine already speaks. `x^{2}` becomes `x²`, `\pi` becomes
 * `π`, and `\frac{a}{b}` becomes `(a)/(b)` with brackets only where they change the meaning.
 *
 * ## Why it re-prints from the parsed tree
 *
 * The alternative is a string rewrite of the LaTeX, and it would be wrong in the one case that
 * matters: `\frac{x+1}{x-1}` written inline needs brackets that the source does not contain,
 * and `\frac{x}{2}` does not. Only something that knows the tree knows which is which. Working
 * from the parse also means anything this prints is, by construction, something the evaluator
 * accepted.
 */

/** Operator binding strength, so brackets are added exactly where they change the reading. */
const PRECEDENCE = { '+': 1, '-': 1, '*': 2, '/': 2, '^': 3 } as const;

/**
 * The label for a formula, in the markup the text engine understands.
 *
 * Returns `null` when the formula does not parse — the caller draws nothing rather than a
 * broken string, which is the same stance every other value takes.
 */
export function formulaLabel(expression: string): string | null {
  try {
    return `y = ${print(parseExpression(expression), 0)}`;
  } catch {
    return null;
  }
}

function print(node: Node, parentPrecedence: number): string {
  switch (node.kind) {
    case 'number':
      return format(node.value);

    case 'variable':
      return 'x';

    case 'unary': {
      const inner = print(node.operand, PRECEDENCE['*']);
      return parentPrecedence > PRECEDENCE['+'] ? `(-${inner})` : `-${inner}`;
    }

    case 'call': {
      const args = node.args.map((argument) => print(argument, 0)).join(', ');
      // `sqrt` is the one call with a symbol of its own; the label engine has the glyph.
      if (node.name === 'sqrt') return `\\sqrt{}(${args})`;
      return `${node.name}(${args})`;
    }

    case 'binary': {
      const own = PRECEDENCE[node.operator];

      /*
       * An exponent goes into the markup's superscript rather than being written with a caret,
       * so the label reads `x²` — which is what the formula looks like on paper and the whole
       * reason a label is worth drawing at all.
       */
      if (node.operator === '^') {
        return `${print(node.left, own + 1)}^{${print(node.right, 0)}}`;
      }

      const left = print(node.left, own);
      /*
       * The right operand of a subtraction or a division binds one level tighter.
       *
       * Without this, `x - (y - z)` prints as `x - y - z` and `a/(b*c)` as `a/b*c` — both of
       * which parse back to something different from what was drawn. Since the label sits
       * beside the curve, a reader would compare the two and find they disagree.
       */
      const rightPrecedence = node.operator === '-' || node.operator === '/' ? own + 1 : own;
      const right = print(node.right, rightPrecedence);

      const text = `${left} ${symbolFor(node.operator)} ${right}`;
      return parentPrecedence > own ? `(${text})` : text;
    }
  }
}

function symbolFor(operator: '+' | '-' | '*' | '/'): string {
  // `·` and `÷` rather than `*` and `/`: this is a label for a reader, not source for a parser.
  return operator === '*' ? '·' : operator === '/' ? '/' : operator;
}

/**
 * A number as a reader expects it.
 *
 * `π` and `e` are printed as themselves rather than as 3.14159 — the label is meant to be
 * compared with the formula the lesson is about, and a decimal expansion breaks that.
 */
function format(value: number): string {
  if (Math.abs(value - Math.PI) < 1e-12) return '\\pi{}';
  if (Math.abs(value - Math.E) < 1e-12) return 'e';

  // Trimmed rather than rounded: `0.5` stays exact, `1/3` does not pretend to be.
  return Number(value.toPrecision(10)).toString();
}

/** The function names, for the panel's help text. Sorted so the list is stable to read. */
export const FUNCTION_NAMES: readonly string[] = Object.keys(FUNCTIONS).sort();
